import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { title } from 'process';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api, fetcher } from '@plugins/axios/api';

import { useAlert } from '@hooks/useAlert';

import { Button } from '@components/button/Button';
import { TableInputField } from '@components/field/TableInputField';
import { BasicTable } from '@components/table/BasicTable';
import { PageTitle } from '@components/title/PageTitle';

import { PageInfo, Paginated } from '@mocks/api';
import { ITEM_LIST, LEGACY_ITEM_LIST, ORDER_STATE } from '@mocks/projects';

import ItemSampleImg from '@assets/imgs/items/item_sample.png';

export const POrderDetailPage = () => {
  const [orderDetail, setOrderDetail] = useState<{
    [name: string]: any;
  }>({});
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { data, refetch, isFetching, isError, error } = useQuery<any>({
    queryKey: [`/partner/orders/${orderId}`],
    queryFn: fetcher,
    staleTime: 10 * 1000,
    enabled: orderId != undefined,
    retry: false,
    throwOnError(error, query) {
      if ('response' in error && (error as any)['response']['status'] == 403) {
        // 접근 권한이 없는 경우 처리
        navigate('/partner/main');
        return false;
      }
      return true;
    },
  });

  const { confirmClick } = useAlert();

  const acceptOrderFn = useCallback(() => {
    confirmClick(
      { title: '주문을 접수하시겠습니까?' },
      {
        onClick: () => {
          api
            .post(`/partner/orders/${orderId}/accept`)
            .then((res) => {
              refetch();
            })
            .catch((err) => {
              refetch();
              console.log(err);
            });
        },
      },
    );
  }, [orderId, refetch]);

  const progressOrderFn = useCallback(() => {
    confirmClick(
      { title: '배송을 시작하시겠습니까?' },
      {
        onClick: () => {
          api
            .post(`/partner/orders/${orderId}/progress`)
            .then((res) => {
              refetch();
            })
            .catch((err) => {
              refetch();
              console.log(err);
            });
        },
      },
    );
  }, [orderId, refetch]);

  const completeOrderFn = useCallback(() => {
    confirmClick(
      { title: '배송을 완료하시겠습니까?' },
      {
        onClick: () => {
          api
            .post(`/partner/orders/${orderId}/complete`)
            .then((res) => {
              refetch();
            })
            .catch((err) => {
              refetch();
              console.log(err);
            });
        },
      },
    );
  }, [orderId, refetch]);

  const tableInputCell = useCallback(
    (info: any) => {
      const itemId = info.getValue();
      return (
        <div className='flex flex-row'>
          <TableInputField
            className='w-[60px] border-none text-right'
            id={itemId}
            type='number'
            placeholder='0'
            disabled={orderId != undefined}
            initData={orderDetail[itemId] ?? 0}
            setData={setOrderDetail}
          />
          <p className='self-center pr-1 pt-[2px] text-gray-500'>박스</p>
        </div>
      );
    },
    [orderDetail],
  );

  const orderDetailList = useMemo(() => {
    let detaiList: number[] = [];
    Object.entries(orderDetail).forEach(([k, v]) => {
      if (v != undefined && v != 0 && v != '') {
        detaiList = detaiList.concat(Array(Number(v)).fill(k));
      }
    });
    return detaiList;
  }, [orderDetail]);

  const itemList = useMemo(() => {
    let isLegacy = false;

    if (data && data['item'] && data['item']['insert_at']) {
      const insertAt = new Date(data['item']['insert_at']);
      const cutoffDate = new Date('2025-05-01');

      if (insertAt < cutoffDate) {
        isLegacy = true;
      }
    }

    const TMP_ITEM_LIST = isLegacy ? LEGACY_ITEM_LIST : ITEM_LIST;

    const keys = Object.keys(orderDetail);
    if (keys.length === 0) {
      return TMP_ITEM_LIST;
    } else {
      return TMP_ITEM_LIST.filter((item) =>
        keys.includes(String(item.item_id)),
      );
    }
  }, [orderDetail, data]);

  useEffect(() => {
    if (!isFetching && data != undefined) {
      console.log(data);

      if ('item' in data && 'order_items' in data['item']) {
        const orderItems: Array<number> = data['item']['order_items'] ?? [];
        const tmpOrderDetail: { [name: string]: number } = {};
        orderItems.forEach((itemId: number) => {
          if (itemId in tmpOrderDetail) {
            tmpOrderDetail[itemId] += 1;
          } else {
            tmpOrderDetail[itemId] = 1;
          }
        });
        setOrderDetail((prev) => ({ ...prev, ...tmpOrderDetail }));
      }
    }
  }, [isFetching, data]);

  useEffect(() => {
    if (orderId != undefined) {
      refetch();
    }
  }, [orderId]);

  return (
    <div className='container'>
      <PageTitle className='mt-4' titleHidden />
      <h1 className='text-2xl font-bold leading-[170%] tracking-tight'>
        주문 관리
        {data && data['item']['order_state'] == 0 && ' (미접수)'}
        {data && data['item']['order_state'] == 200 && ' (접수)'}
      </h1>
      {data != undefined && (
        <section className='mt-4 flex flex-row flex-wrap rounded-lg border border-gray-200 p-4'>
          {[
            {
              title: '주문번호',
              value: data['item']['idx'],
            },
            {
              title: '총 수량',
              value: orderDetailList.length + ' 박스',
            },
          ].map((val, i) => {
            return (
              <div key={`menu-${i}`} className='mb-3 flex w-1/2 flex-row'>
                <p className='text-base font-bold'>{val['title']}</p>
                <p className='flex-1 text-center text-base'>{val['value']}</p>
              </div>
            );
          })}
          <div className='mb-3 flex w-full flex-row'>
            <p className='w-1/4 text-base font-bold'>주문상태</p>
            <p
              className={
                ORDER_STATE[data['item']['order_state']]['lable-class']
              }
            >
              {ORDER_STATE[data['item']['order_state']]['title']}
            </p>
          </div>
          <div className='mb-3 flex w-full flex-row'>
            <p className='w-1/4 text-base font-bold'>주문자</p>
            <p className='flex-1 text-start text-base'>
              {data['item']['user_name']}
            </p>
          </div>
          <div className='mb-3 flex w-full flex-row'>
            <p className='w-1/4 text-base font-bold'>연락처</p>
            <p className='flex-1 text-start text-base'>
              {data['item']['user_phone']}
            </p>
          </div>
          <div className='flex w-full flex-row'>
            <p className='w-1/4 text-base font-bold'>주소</p>
            <p className='flex-1 text-start text-base'>
              {data['item']['user_address']}
            </p>
          </div>
        </section>
      )}
      {data != undefined && (
        <section className='mt-3 flex flex-row flex-wrap space-y-5 rounded-lg p-4'>
          {[
            {
              title: '주문일자',
              value: format(
                new Date(data['item']['insert_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '접수일자',
              value: format(
                new Date(data['item']['state_accept_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '배송중',
              value: format(
                new Date(data['item']['state_complete_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
            {
              title: '배송완료',
              value: format(
                new Date(data['item']['state_complete_at']),
                'yyyy-MM-dd HH시 mm분',
              ),
            },
          ]
            .filter((_, i) => {
              let idxList = [];

              if (data['item']['order_state'] == 0) {
                idxList = [0];
              } else if (data['item']['order_state'] == 200) {
                idxList = [0, 1];
              } else if (data['item']['order_state'] == 202) {
                idxList = [0, 1, 2];
              } else if (data['item']['order_state'] == 300) {
                idxList = [0, 1, 3];
              } else {
                idxList = [0];
              }
              return idxList.includes(i);
            })
            .map((val, i) => {
              return (
                <div
                  key={`menu-${i}`}
                  className='flex w-full flex-row before:mr-3 before:inline-block before:h-2 before:w-2 before:self-center before:rounded-full before:bg-gray-500 last:before:bg-red-500'
                >
                  <p className='w-1/4 text-base font-bold'>{val['title']}</p>
                  <p className='flex-1 text-start text-base'>{val['value']}</p>
                </div>
              );
            })}
        </section>
      )}
      <section className='mt-6'>
        <BasicTable
          captionPosition='top'
          isLoading={isFetching}
          columns={[
            {
              header: '품번',
              accessorKey: 'item_id',
              minSize: 30,
              maxSize: 30,
            },
            {
              header: '품목',
              accessorKey: 'short_name',
              minSize: 140,
              maxSize: 140,
            },
            {
              header: '입수량\n(박스당)',
              accessorKey: 'unit_per',
              minSize: 100,
              maxSize: 100,
            },
            {
              header: '주문수량\n(박스)',
              accessorKey: 'count',
              minSize: 100,
              maxSize: 100,
              cell: tableInputCell,
            },
          ]}
          data={itemList}
          className='flex h-fit w-full flex-col gap-12'
        />
      </section>
      <section className='mt-6 flex flex-row justify-end pb-10'>
        {data && data['item']['order_state'] == 0 && (
          <>
            <Button
              disabled={data && data['item']['order_state'] != 0}
              className='flex-1 rounded-r-none'
              onClick={acceptOrderFn}
            >
              주문 접수
            </Button>
          </>
        )}
        {data && data['item']['order_state'] == 200 && (
          <>
            <Button className='flex-1 rounded-r-none' onClick={progressOrderFn}>
              배송 시작
            </Button>
          </>
        )}
        {data && data['item']['order_state'] == 202 && (
          <>
            <Button className='flex-1 rounded-r-none' onClick={completeOrderFn}>
              배송 완료
            </Button>
          </>
        )}
      </section>
    </div>
  );
};
